// Case Studies section — real project showcase
import { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Clock, Users, ArrowRight, Quote, X } from "lucide-react";
import { useLang } from "../contexts/LangContext";
import { CASE_STUDIES } from "../data/caseStudies";

export default function CaseStudies() {
  const { lang, t } = useLang();
  const ct = t.case_studies;
  const items = CASE_STUDIES[lang] || CASE_STUDIES.de;
  const [active, setActive] = useState(null);

  const closeModal = () => setActive(null);

  return (
    <section className="py-16 lg:py-24 bg-neutral-50 dark:bg-[#0A0B0D]" data-testid="case-studies-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#FF5A00] mb-3">
              {ct.eyebrow}
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mb-3">
              {ct.title}
            </h2>
            <p className="text-sm sm:text-base text-neutral-600 dark:text-neutral-400 max-w-2xl leading-relaxed">
              {ct.subtitle}
            </p>
          </div>
        </div>

        {/* Results KPI strip — makes the section impossible to miss */}
        <div
          className="grid grid-cols-2 lg:grid-cols-4 border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#0F1012] mb-10"
          data-testid="case-studies-kpi-strip"
        >
          <ResultKPI value={ct.hero_kpi1_value} sub={ct.hero_kpi1_sub} accent />
          <ResultKPI value={ct.hero_kpi2_value} sub={ct.hero_kpi2_sub} />
          <ResultKPI value={ct.hero_kpi3_value} sub={ct.hero_kpi3_sub} />
          <ResultKPI value={ct.hero_kpi4_value} sub={ct.hero_kpi4_sub} accent />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((cs) => (
            <button
              key={cs.id}
              id={cs.id}
              type="button"
              onClick={() => setActive(cs)}
              className="group text-start bg-white dark:bg-[#0F1012] border border-neutral-200 dark:border-neutral-800 overflow-hidden hover:border-[#FF5A00] transition-all duration-300 flex flex-col scroll-mt-24"
              data-testid={`case-study-card-${cs.id}`}
            >
              <div className="relative aspect-[16/10] overflow-hidden bg-neutral-200">
                <img
                  src={cs.image}
                  alt={cs.title}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute top-3 start-3 bg-[#FF5A00] text-white text-[10px] font-bold tracking-wider uppercase px-2.5 py-1">
                  {cs.tag}
                </div>
                <div className="absolute bottom-3 end-3 bg-white/95 dark:bg-black/80 text-neutral-900 dark:text-white text-[10px] font-bold tracking-widest uppercase px-2.5 py-1">
                  {cs.service_label}
                </div>
              </div>
              <div className="p-5 flex flex-col flex-1">
                <h3 className="text-lg font-black tracking-tight leading-snug mb-2 line-clamp-2">
                  {cs.title}
                </h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 line-clamp-3 leading-relaxed mb-4">
                  {cs.summary}
                </p>
                <div className="grid grid-cols-3 gap-2 mt-auto border-t border-neutral-100 dark:border-neutral-800 pt-4">
                  <Stat icon={Clock} value={cs.stats.value1} label={cs.stats.label1} />
                  <Stat icon={Users} value={cs.stats.value3} label={cs.stats.label3} />
                  <Stat icon={MapPin} value={cs.stats.value2} label={cs.stats.label2} />
                </div>
                <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-[#FF5A00] uppercase tracking-wider">
                  {ct.read_full}
                  <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Detail Modal */}
      {active && (
        <div
          className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto animate-[fadeIn_.3s_ease-out]"
          onClick={closeModal}
          data-testid="case-study-modal"
        >
          <div
            className="relative bg-white dark:bg-[#0F1012] max-w-3xl w-full my-8 max-h-[calc(100vh-4rem)] overflow-y-auto animate-[scaleIn_.4s_cubic-bezier(.34,1.56,.64,1)]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeModal}
              aria-label="Close"
              className="absolute top-3 end-3 z-10 w-10 h-10 flex items-center justify-center bg-white/90 dark:bg-black/70 hover:bg-white text-neutral-700 dark:text-white transition"
              data-testid="case-study-modal-close"
            >
              <X size={18} />
            </button>
            <div className="aspect-[16/9] bg-neutral-200">
              <img src={active.image} alt={active.title} className="w-full h-full object-cover" />
            </div>
            <div className="p-7 sm:p-10">
              <div className="flex items-center gap-2 mb-3">
                <span className="bg-[#FF5A00] text-white text-[10px] font-bold tracking-wider uppercase px-2.5 py-1">
                  {active.tag}
                </span>
                <span className="text-xs text-neutral-500 inline-flex items-center gap-1">
                  <MapPin size={12} /> {active.location}
                </span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-black tracking-tighter leading-tight mb-4">
                {active.title}
              </h3>
              <p className="text-base text-neutral-600 dark:text-neutral-400 leading-relaxed mb-6">
                {active.summary}
              </p>

              <div className="grid grid-cols-3 gap-3 py-5 my-6 border-y border-neutral-200 dark:border-neutral-800">
                <BigStat value={active.stats.value1} label={active.stats.label1} />
                <BigStat value={active.stats.value2} label={active.stats.label2} />
                <BigStat value={active.stats.value3} label={active.stats.label3} accent />
              </div>

              <Block title={ct.challenge_label} body={active.challenge} />
              <Block title={ct.solution_label} body={active.solution} />
              <Block title={ct.outcome_label} body={active.outcome} />

              <div className="flex flex-wrap gap-2 my-6">
                {active.services_included.map((s) => (
                  <span
                    key={s}
                    className="text-[11px] font-bold tracking-wider uppercase bg-neutral-100 dark:bg-neutral-900 text-neutral-700 dark:text-neutral-300 px-3 py-1.5"
                  >
                    {s}
                  </span>
                ))}
              </div>

              <blockquote className="bg-neutral-50 dark:bg-neutral-900 border-s-4 border-[#FF5A00] p-5 my-6">
                <Quote size={18} className="text-[#FF5A00] mb-2" />
                <p className="text-base italic text-neutral-800 dark:text-neutral-200 leading-relaxed mb-3">
                  {active.quote}
                </p>
                <footer className="text-xs font-bold tracking-wider uppercase text-neutral-500">
                  — {active.quote_author}
                </footer>
              </blockquote>

              <div className="flex items-center justify-between gap-4 mt-6 pt-6 border-t border-neutral-200 dark:border-neutral-800">
                <div>
                  <div className="text-[11px] font-bold tracking-widest uppercase text-neutral-500 mb-1">{ct.custom_offer_label}</div>
                  <div className="text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                    {ct.custom_offer_body}
                  </div>
                </div>
                <Link
                  to="/book"
                  onClick={closeModal}
                  className="flex-shrink-0 bg-[#FF5A00] hover:bg-[#E04F00] text-white font-bold tracking-wider uppercase text-xs px-5 py-3 inline-flex items-center gap-2 transition-colors"
                  data-testid="case-study-modal-book"
                >
                  {ct.book_similar}
                  <ArrowRight size={12} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function Stat({ icon: Icon, value, label }) {
  return (
    <div>
      <Icon size={12} className="text-neutral-400 mb-1" />
      <div className="text-sm font-black tracking-tight tabular-nums">{value}</div>
      <div className="text-[9px] font-bold tracking-widest uppercase text-neutral-500 mt-0.5">{label}</div>
    </div>
  );
}

function BigStat({ value, label, accent }) {
  return (
    <div className="text-center">
      <div className={`text-2xl font-black tracking-tighter tabular-nums ${accent ? "text-[#FF5A00]" : ""}`}>
        {value}
      </div>
      <div className="text-[10px] font-bold tracking-widest uppercase text-neutral-500 mt-1">{label}</div>
    </div>
  );
}

function Block({ title, body }) {
  return (
    <div className="mb-5">
      <h4 className="text-xs font-bold tracking-[0.22em] uppercase text-[#FF5A00] mb-2">{title}</h4>
      <p className="text-sm sm:text-[15px] text-neutral-700 dark:text-neutral-300 leading-relaxed">{body}</p>
    </div>
  );
}

function ResultKPI({ value, sub, accent = false }) {
  return (
    <div
      className={`p-6 lg:p-8 border-neutral-200 dark:border-neutral-800 ${
        accent ? "bg-[#FF5A00] text-white" : "bg-transparent"
      } border-b lg:border-b-0 lg:border-e last:border-e-0 [&:nth-child(2)]:border-b lg:[&:nth-child(2)]:border-b-0`}
    >
      <div className={`text-4xl sm:text-5xl font-black tracking-tighter tabular-nums leading-none ${accent ? "text-white" : "text-neutral-900 dark:text-white"}`}>
        {value}
      </div>
      <div className={`text-xs font-semibold leading-snug mt-3 ${accent ? "text-white/90" : "text-neutral-600 dark:text-neutral-400"}`}>
        {sub}
      </div>
    </div>
  );
}
